import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface TabItem {
  id: string;
  label: string;
  icon?: IconDefinition;
  /** Rendered as a pill next to the label; hidden when 0 / empty. */
  badge?: number | string;
  /** When set the tab renders as a react-router `<Link>` instead of a `<button>`. */
  to?: string;
}

interface TabsProps {
  items: TabItem[];
  activeId: string;
  /** Required for button tabs, ignored for `<Link>` tabs. */
  onChange?: (id: string) => void;
  className?: string;
}

const ITEM_BASE =
  'shrink-0 py-3 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center gap-2 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset';

const ITEM_ACTIVE = 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400';

const ITEM_INACTIVE =
  'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 ' +
  'hover:border-gray-300 dark:hover:border-gray-600';

function badgeIsVisible(badge: number | string | undefined): boolean {
  if (badge === undefined) return false;
  if (typeof badge === 'number') return badge > 0;
  return badge !== '';
}

export default function Tabs({ items, activeId, onChange, className = '' }: TabsProps) {
  return (
    <nav
      className={`flex space-x-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-none ${className}`}
      aria-label="Unternavigation"
    >
      {items.map((item) => {
        const isActive = item.id === activeId;
        const classes = `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_INACTIVE}`;
        const content = (
          <>
            {item.icon !== undefined && <FontAwesomeIcon icon={item.icon} className="w-4 h-4" />}
            <span>{item.label}</span>
            {badgeIsVisible(item.badge) && (
              <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">{item.badge}</span>
            )}
          </>
        );

        if (item.to !== undefined) {
          return (
            <Link key={item.id} to={item.to} className={classes} aria-current={isActive ? 'page' : undefined}>
              {content}
            </Link>
          );
        }

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onChange?.(item.id)}
            className={classes}
            aria-current={isActive ? 'true' : undefined}
          >
            {content}
          </button>
        );
      })}
    </nav>
  );
}
